import { FileTask } from "@components/Files/FileTask"
import * as tus from "tus-js-client"
import { v4 } from "uuid"

/**
 * TUS protocol file upload handler with comprehensive error logging for Sentry
 *
 * Error Logging Strategy:
 * 1. Simple string logs (console.error with string) - captured by Sentry's CaptureConsole
 * 2. Detailed object logs (console.error with object) - for browser debugging
 * 3. Flat error structure - all fields at root level to avoid "[Object]" in Sentry
 * 4. Error categorization - Network/CORS, Server, Client based on HTTP status
 *
 * Why this approach:
 * - Sentry's CaptureConsole works best with simple strings
 * - Nested objects appear as "[Object]" in Sentry logs
 * - Two logs provide both Sentry visibility and local debugging capability
 */
export class TusFileTask extends FileTask {
    file = null
    result = null
    progress = 0
    fileName = ""
    status = "progress"
    tusUploader = null
    canRetry = true
    onlyUpload = false

    // ========== LIFECYCLE ==========

    constructor(file) {
        super(file, false)

        this.onlyUpload = file?.meta?.only_upload
        this.file = file
        this.fileName = file.name

        this.createTus(file).then(() => {
            this.tusUploader.start()
        })
    }

    async createTus(file) {
        let formData = new FormData()
        const model = file?.meta?.model || localStorage.getItem("file_model")

        console.debug("model", model)

        formData.append("model", model)
        formData.append("cabinet_id", localStorage.getItem("cabinet_id"))

        this.tusUploader = new tus.Upload(file, {
            endpoint: window.file_storage_tus_url,
            retryDelays: [0, 3000],
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${window.token}`
            },
            metadata: {
                filename: v4(),
                original_name: file.name,
                mime_type: file.type,
                ...Object.fromEntries(formData)
            },

            // eslint-disable-ne    xt-line no-unused-vars
            onShouldRetry: function (err, retryAttempt, options) {
                const progressEventDetails = this.extractProgressEventDetails(err)
                const status = err.originalResponse
                    ? err.originalResponse.getStatus()
                    : progressEventDetails?.target?.status || 0

                console.error(
                    `TUS Upload Error (Retry ${retryAttempt}): ${err?.message} | File: ${this.fileName} | Status: ${status}`
                )

                return !(status === 403 || status === 422)
            }.bind(this),
            onError: function (e) {
                // Extract all available error details from TUS error and ProgressEvent
                const progressEventDetails = this.extractProgressEventDetails(e)
                const status = progressEventDetails?.target?.status || 0
                const errorType = this.determineErrorType(status)

                // Build flat error object - all fields at root level for Sentry visibility
                const flatError = this.buildFlatErrorObject(e, progressEventDetails, status, errorType)

                // Log for Sentry (string) and browser console (full object)
                const errorMessage = `TUS Upload Error [${errorType}]${
                    e.originalRequest?._url ? ` | URL: ${e.originalRequest._url}` : ""
                }`
                this.logErrorDetails(errorMessage, e?.message, status, flatError)

                this.status = "failed"
                this.progress = 0
                this.message = this.parseResponseBody(e)

                if (this.onError) {
                    const error = this.createErrorObject(this.message, e, progressEventDetails)
                    this.onError(error)
                }
            }.bind(this),
            onProgress: function (bytesUploaded, bytesTotal) {
                this.onProgress({ progress: bytesUploaded / bytesTotal })
            }.bind(this),
            onSuccess: function (payload) {
                const uuid = payload.lastResponse.getHeader("X-File-Uuid")
                this.onSuccess({ uuid: uuid })
            }.bind(this)
        })
    }

    // ========== PUBLIC API ==========

    retry() {
        if (this.tusUploader) {
            this.tusUploader.start()
        }
    }

    abort() {
        this.tusUploader.abort()

        if (this.onError) {
            this.status = "aborted"
            this.onError("aborted")
        }
    }

    // ========== CALLBACKS ==========

    onProgress(data) {
        this.status = "progress"
        this.progress = parseInt(data?.progress * 100)
    }

    onSuccess(data) {
        this.result = data

        if (this.onlyUpload) {
            this.status = "completed"
            this.onComplete(this.result)
            return
        }

        this.status = "checking"
        this.checkStatus()

        this.progress = 100
    }

    onFailed(data) {
        this.status = "failed"
        this.message = data?.message
        this.progress = 0

        const flatError = this.buildFlatErrorBase("TUS_Task_Failed", data?.message, {
            dataResponse: data?.response
        })

        this.logErrorDetails("TUS Task Failed", data?.message, null, flatError)

        if (this.onError) {
            const error = this.createErrorWithContext(data?.message || "TUS task failed", data, flatError)
            this.onError(error)
        }
    }

    // ========== ERROR HANDLING HELPERS ==========

    /**
     * Extract detailed information from ProgressEvent
     * ProgressEvent is nested inside TUS error and contains XHR state
     * We flatten it to make all fields visible in Sentry
     */
    extractProgressEventDetails(err) {
        return err.causingError
            ? {
                  type: err.causingError.type,
                  loaded: err.causingError.loaded,
                  total: err.causingError.total,
                  lengthComputable: err.causingError.lengthComputable,
                  target: err.causingError.target
                      ? {
                            readyState: err.causingError.target.readyState,
                            status: err.causingError.target.status,
                            statusText: err.causingError.target.statusText,
                            responseURL: err.causingError.target.responseURL,
                            responseText: err.causingError.target.responseText,
                            response: err.causingError.target.response,
                            withCredentials: err.causingError.target.withCredentials,
                            timeout: err.causingError.target.timeout
                        }
                      : null
              }
            : null
    }

    /**
     * Categorize error by HTTP status code
     * status=0 typically means network/CORS issue (request didn't reach server)
     */
    determineErrorType(status) {
        if (status === 0) return "Network/CORS"
        if (status >= 500) return "Server"
        if (status >= 400) return "Client"
        return "Unknown"
    }

    /**
     * Build comprehensive flat error object for TUS uploads
     * Inherits base structure from parent, adds TUS-specific fields
     * All fields at root level to avoid "[Object]" in Sentry
     */
    buildFlatErrorObject(e, progressEventDetails, status, errorType) {
        return this.buildFlatErrorBase(errorType, e?.message, {
            errorName: e?.name,
            endpoint: window.file_storage_tus_url,
            requestUrl: e.originalRequest?._url,
            requestMethod: e.originalRequest?._method,
            httpStatus: status,
            httpStatusText: progressEventDetails?.target?.statusText,
            xhrReadyState: progressEventDetails?.target?.readyState,
            xhrResponseURL: progressEventDetails?.target?.responseURL,
            xhrResponseText: progressEventDetails?.target?.responseText?.substring(0, 500),
            xhrWithCredentials: progressEventDetails?.target?.withCredentials,
            eventType: progressEventDetails?.type,
            eventLoaded: progressEventDetails?.loaded,
            eventTotal: progressEventDetails?.total
        })
    }

    parseResponseBody(e) {
        if (typeof e.originalResponse?.getBody === "function") {
            try {
                return JSON.parse(e.originalResponse.getBody())
            } catch (err) {
                return err
            }
        }
        return e
    }

    createErrorObject(message, originalError, progressEventDetails) {
        const context = {
            fileName: this.fileName,
            originalMessage: originalError?.message,
            responseBody: message,
            progressEvent: progressEventDetails,
            requestDetails: originalError.originalRequest
                ? {
                      method: originalError.originalRequest._method,
                      url: originalError.originalRequest._url
                  }
                : null
        }
        return this.createErrorWithContext(message?.message || message || "TUS upload failed", originalError, context)
    }
}
